import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const KEY = 'adrodd.firstTouch';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

/**
 * First-touch acquisition: the external referrer and utm_* of the visitor's
 * first page load, kept until they sign up. The signup request itself only
 * ever carries our own origin as Referer, so without this every signup reads
 * as "direct". Stored as a query string (URLSearchParams) so it can be
 * forwarded verbatim as a header or appended to an OAuth redirect URL.
 */
export function captureFirstTouch(): void {
  try {
    if (localStorage.getItem(KEY) !== null) return;
    const params = new URLSearchParams();
    const search = new URLSearchParams(window.location.search);
    for (const k of UTM_KEYS) {
      const v = search.get(k);
      if (v) params.set(k, v);
    }
    const ref = document.referrer;
    if (ref && new URL(ref).host !== window.location.host) params.set('referrer', ref);
    localStorage.setItem(KEY, params.toString());
  } catch {
    // Storage blocked or unparseable referrer: attribution is best-effort.
  }
}

export function readFirstTouch(): string {
  try {
    return localStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

/** Forward the stored first touch as `X-First-Touch` on every API request. */
export function applyFirstTouchInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const touch = readFirstTouch();
    if (touch) config.headers['X-First-Touch'] = touch;
    return config;
  });
}
